import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Globe, Phone, Mail, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";

export default function BusinessDetail() {
  const { id } = useParams();
  const [b, setB] = useState(null);
  const { user } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", party_size: 1, consent: true });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    api.get(`/businesses/${id}`).then((r) => setB(r.data)).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, name: user.name || "", email: user.email || "" }));
  }, [user]);

  if (!b) return <div className="p-10 text-muted-foreground">Loading…</div>;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/business-enquiries", {
        business_id: b.id,
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        party_size: Number(form.party_size) || 1,
        message: form.message,
        consent: form.consent,
      });
      setSent(true);
      toast.success("Enquiry sent");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Please try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-5 md:px-8 py-10 md:py-14" data-testid="business-detail">
      <div className="card-soft overflow-hidden">
        {b.image_url && <div className="h-40 md:h-56 bg-cover bg-center" style={{ backgroundImage: `url(${b.image_url})` }} />}
        <div className="p-6 md:p-8">
          <div className="flex items-center gap-2 flex-wrap">
            {b.featured && <span className="text-[10px] uppercase tracking-widest bg-terracotta-500 text-white px-2 py-0.5 rounded-full">Featured</span>}
            {b.verified && <span className="text-[10px] uppercase tracking-widest bg-teal-800 text-white px-2 py-0.5 rounded-full">Verified</span>}
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
              {b.business_type.replace(/_/g, " ")}
            </span>
          </div>
          <h1 className="font-display text-3xl md:text-4xl text-teal-900 mt-2" data-testid="biz-name">{b.name}</h1>
          <div className="text-sm text-muted-foreground">{b.town}, {b.region}, {b.country}</div>
          <p className="mt-4 text-teal-900">{b.description}</p>

          <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div><div className="overline">Services</div><div className="mt-1 text-teal-900">{(b.services || []).join(", ") || "—"}</div></div>
            <div><div className="overline">Equipment</div><div className="mt-1 text-teal-900">{(b.equipment || []).join(", ") || "—"}</div></div>
            <div><div className="overline">Ability levels</div><div className="mt-1 text-teal-900 capitalize">{(b.ability_levels || []).map((a) => a.replace(/_/g, " ")).join(", ") || "—"}</div></div>
          </div>

          <div className="mt-5 flex flex-wrap gap-4 text-sm">
            {b.website && (
              <a href={b.website} target="_blank" rel="noreferrer" data-testid="biz-website"
                className="text-teal-800 flex items-center gap-1 hover:underline">
                <Globe className="w-4 h-4" /> Website
              </a>
            )}
            {b.phone && <span className="text-teal-800 flex items-center gap-1"><Phone className="w-4 h-4" /> {b.phone}</span>}
            {b.email && <span className="text-teal-800 flex items-center gap-1"><Mail className="w-4 h-4" /> {b.email}</span>}
          </div>
        </div>
      </div>

      <div className="mt-6 card-soft p-6 md:p-8" data-testid="biz-enquiry-form">
        <div className="overline">Enquire</div>
        <h2 className="font-display text-2xl text-teal-900 mt-1">Send this business a message</h2>
        {sent ? (
          <div className="mt-4 rounded-xl bg-sand-100 border border-border p-4 text-sm text-teal-900" data-testid="biz-enquiry-success">
            Thanks — your enquiry has been recorded. The business will be in touch.
          </div>
        ) : (
          <form onSubmit={submit} className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Your name</label>
              <Input required data-testid="enq-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 mt-1" />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Email</label>
              <Input required data-testid="enq-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11 mt-1" />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Phone (optional)</label>
              <Input data-testid="enq-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-11 mt-1" />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Party size</label>
              <Input data-testid="enq-party" type="number" min={1} value={form.party_size} onChange={(e) => setForm({ ...form, party_size: e.target.value })} className="h-11 mt-1" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-semibold text-muted-foreground">Message</label>
              <textarea
                required
                data-testid="enq-message"
                className="mt-1 w-full h-28 rounded-xl border border-border bg-white p-3 text-sm outline-none focus:border-teal-800"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
              />
            </div>
            <label className="md:col-span-2 flex items-center gap-2 text-xs text-muted-foreground">
              <input
                type="checkbox"
                data-testid="enq-consent"
                checked={form.consent}
                onChange={(e) => setForm({ ...form, consent: e.target.checked })}
              />
              I consent to Surfmatics passing my details to this business.
            </label>
            <div className="md:col-span-2 flex justify-end">
              <button
                type="submit"
                data-testid="enq-submit"
                disabled={busy || !form.consent}
                className="h-11 px-6 rounded-full bg-terracotta-500 text-white text-sm font-medium hover:bg-terracotta-600 disabled:opacity-60 flex items-center gap-2"
              >
                {busy ? "Sending…" : "Send enquiry"}
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
